<template>
  <section class="products-page">
    <a-tabs v-model:active-key="kind" class="product-tabs">
      <a-tab-pane key="all" :tab="`全部 (${store.products.length})`" />
      <a-tab-pane
        v-for="item in productKindOptions"
        :key="item.value"
        :tab="`${item.label} (${countKind(item.value)})`"
      />
    </a-tabs>
    <div class="product-toolbar">
      <a-input-search
        v-model:value="query"
        allow-clear
        placeholder="搜索商品名称或 ID"
        aria-label="搜索商品"
      />
      <label class="filter-label" for="commerce-product-status">筛选上架状态</label>
      <a-select id="commerce-product-status" v-model:value="status" :options="statusOptions" />
      <label class="filter-label" for="commerce-product-mode">筛选购买方式</label>
      <a-select id="commerce-product-mode" v-model:value="purchaseMode" :options="modeOptions" />
      <a-button :loading="store.loading" @click="store.load"
        ><i class="ri-refresh-line" />刷新</a-button
      >
      <a-button v-if="canManage" type="primary" @click="openForm()"
        ><i class="ri-add-line" />新增商品</a-button
      >
    </div>
    <p class="product-note">上架状态决定商品是否对外销售；支付渠道关闭时仍可维护商品。</p>
    <a-result v-if="store.error" status="error" title="商品加载失败" :sub-title="store.error">
      <template #extra><a-button type="primary" @click="store.load">重新加载</a-button></template>
    </a-result>
    <a-table
      v-else
      :data-source="visibleProducts"
      :columns="columns"
      :loading="store.loading"
      row-key="id"
      :scroll="{ x: 1040 }"
      :pagination="{ pageSize: 20, hideOnSinglePage: true }"
      :row-expandable="isMembershipProduct"
    >
      <template #bodyCell="{ column, record }">
        <div v-if="column.key === 'product'" class="product-cell">
          <strong>{{ record.name }}</strong
          ><small>{{ productKindLabel(record.kind) }}</small>
        </div>
        <span v-else-if="column.key === 'grant'">{{ productGrantSummary(record) }}</span>
        <div v-else-if="column.key === 'price'" class="product-cell product-price">
          <span>{{ productPrice(record) }}</span
          ><small>{{ purchaseModeLabel(record) }}</small>
        </div>
        <span v-else-if="column.key === 'validity'">{{ productValidity(record) }}</span>
        <a-tag v-else-if="column.key === 'enabled'" :color="record.enabled ? 'green' : 'default'">{{
          record.enabled ? '已上架' : '已下架'
        }}</a-tag>
        <a-button
          v-else-if="column.key === 'actions' && canManage"
          size="small"
          @click="openForm(record)"
          >编辑</a-button
        >
      </template>
      <template #expandedRowRender="{ record }">
        <a-descriptions title="会员完整权益" size="small" :column="{ xs: 1, sm: 2, lg: 3 }">
          <a-descriptions-item label="工作台上限">{{
            record.grant.capacity?.maxWorkspaces ?? '—'
          }}</a-descriptions-item>
          <a-descriptions-item label="单工作台组件上限">{{
            record.grant.capacity?.maxComponentInstances ?? '—'
          }}</a-descriptions-item>
          <a-descriptions-item label="存储额度">{{
            storageLabel(record.grant.quota?.storageBytes ?? 0)
          }}</a-descriptions-item>
          <a-descriptions-item label="每月下载流量">{{
            storageLabel(record.grant.quota?.transferBytes ?? 0)
          }}</a-descriptions-item>
          <a-descriptions-item label="数据记录额度"
            >{{ numberLabel(record.grant.quota?.recordCount ?? 0) }} 行</a-descriptions-item
          >
          <a-descriptions-item label="每周期积点（Credits）">{{
            numberLabel(record.grant.quota?.aiCredits ?? 0)
          }}</a-descriptions-item>
          <a-descriptions-item v-if="record.kind === 'team_plan'" label="包含席位">{{
            record.grant.seatCount
          }}</a-descriptions-item>
          <a-descriptions-item label="商品说明">{{
            record.description || '暂无说明'
          }}</a-descriptions-item>
          <a-descriptions-item label="商品 ID">{{ record.id }}</a-descriptions-item>
        </a-descriptions>
      </template>
      <template #emptyText
        ><a-empty
          :description="
            store.products.length
              ? '没有匹配的商品，请调整筛选条件'
              : '暂无商品，可通过新增商品开始配置'
          "
      /></template>
    </a-table>
    <ProductFormDrawer v-model:open="formOpen" :product="editingProduct" />
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { CommerceProductKind, CommerceProductView } from 'shared';
import { useAdminSessionStore } from '@/stores/admin-session.store';
import { useCommerceProductsStore } from '@/stores/commerce-products.store';
import ProductFormDrawer from '@/components/commerce/ProductFormDrawer.vue';
import {
  productKindOptions,
  productKindLabel,
  productGrantSummary,
  productPrice,
  productValidity,
  purchaseModeLabel,
  matchesProduct,
  storageLabel,
  numberLabel,
} from '@/utils/commerce-products';

const store = useCommerceProductsStore();
const session = useAdminSessionStore();
const canManage = computed(() => session.can('commerce.manage'));
const kind = ref<CommerceProductKind | 'all'>('all');
const query = ref('');
const status = ref('all');
const purchaseMode = ref('all');
const formOpen = ref(false);
const editingProduct = ref<CommerceProductView>();
const statusOptions = [
  { value: 'all', label: '全部上架状态' },
  { value: 'enabled', label: '已上架' },
  { value: 'disabled', label: '已下架' },
];
const modeOptions = [
  { value: 'all', label: '全部购买方式' },
  { value: 'one_time', label: '一次性购买' },
  { value: 'auto_renew', label: '自动续费' },
];
const columns = [
  { title: '商品', key: 'product', width: 260 },
  { title: '核心权益', key: 'grant', width: 210 },
  { title: '售价 / 购买方式', key: 'price', width: 170 },
  { title: '有效期', key: 'validity', width: 150 },
  { title: '上架状态', key: 'enabled', width: 100 },
  { title: '展示排序', dataIndex: 'displayOrder', width: 100 },
  { title: '操作', key: 'actions', width: 80 },
];
const visibleProducts = computed(() =>
  store.sortedProducts.filter(
    (product) =>
      (kind.value === 'all' || product.kind === kind.value) &&
      (status.value === 'all' || product.enabled === (status.value === 'enabled')) &&
      (purchaseMode.value === 'all' || product.purchaseMode === purchaseMode.value) &&
      matchesProduct(product, query.value),
  ),
);
function countKind(value: CommerceProductKind): number {
  return store.products.filter((item) => item.kind === value).length;
}
function isMembershipProduct(product: CommerceProductView): boolean {
  return product.kind === 'pro_plan' || product.kind === 'team_plan';
}
function openForm(product?: CommerceProductView): void {
  if (!canManage.value) return;
  editingProduct.value = product;
  formOpen.value = true;
}
onMounted(() => void store.load());
</script>

<style scoped>
.products-page {
  min-width: 0;
}
.filter-label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.product-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.product-toolbar :deep(.ant-input-search) {
  width: 280px;
}
.product-toolbar :deep(.ant-select) {
  min-width: 155px;
}
.product-note {
  margin: 16px 0;
  color: var(--aw-text-secondary);
}
.product-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-wrap: anywhere;
}
.product-cell strong {
  font-weight: 600;
}
.product-cell small {
  color: var(--aw-text-secondary);
}
.product-price {
  font-variant-numeric: tabular-nums;
}
@media (max-width: 760px) {
  .product-toolbar :deep(.ant-input-search) {
    width: 100%;
  }
}
</style>
